/**
 * A simple event subscription and notification utility with configurable event history.
 *
 * The `EventSubscriber` class allows multiple observers to subscribe to events and be notified when events occur.
 * It also supports replaying a configurable number of past events to new subscribers.
 *
 * @typeParam T - The type of data passed to observers when an event is notified.
 *
 * @example
 * ```typescript
 * const eventSubscriber = new EventSubscriber<string>(1);
 * const unsubscribe = eventSubscriber.subscribe((data) => console.log(data));
 * subscriber.notify('Hello');
 * unsubscribe();
 * ```
 *
 * @param historySize - Determines how many past events are stored and replayed to new subscribers:
 * - `0`: No history is kept. New subscribers will only receive future events ("hot observable" behavior).
 * - `1`: Only the most recent event is kept. New subscribers will immediately receive the latest event upon subscribing.
 * - `n > 1`: The last `n` events are kept. New subscribers will receive the last `n` events in the order they occurred.
 * - `Infinity`: All past events are kept and replayed to new subscribers.
 */
export class EventSubscriber<T = void> {
  private readonly observers = new Map<symbol, (data?: T) => void>();
  private readonly history: (T | undefined)[] = [];

  constructor(private readonly historySize: number = 0) {}

  /**
   * Subscribe to the event
   * @param observer Function that will be called when the event occurs
   * @returns Function that can be called to unsubscribe
   */
  public subscribe(observer: (data?: T) => void): () => void {
    const id = Symbol();
    this.observers.set(id, observer);

    if (this.historySize > 0) {
      this.history.forEach((data) => observer(data));
    }

    return () => {
      this.observers.delete(id);
    };
  }

  /**
   * Notify all observers about an event
   * @param data The optional data to pass to all observers
   */
  public notify(data?: T): void {
    // Store in history if historySize > 0
    if (this.historySize > 0) {
      this.history.push(data);

      // Trim history if it exceeds the configured size
      if (this.history.length > this.historySize) {
        this.history.shift();
      }
    }

    this.observers.forEach((observer) => observer(data));
  }
}
